import moment from 'moment';
import store from '@/store';
import valid from 'card-validator';
import { isValidPhoneNumber } from 'react-phone-number-input';
import { cpf as cpfValidator, cnpj } from 'cpf-cnpj-validator';
import { emailSeemsValid } from 'email-seems-valid';
import { ACCEPTED_DOCUMENT_TYPES, UNRECOGNISED_DOCUMENT_TYPE } from 'utils/freeFareDocumentRules';
import getPassengerAge from './getPassengerAge';
import { LAP_CHILD_AGE_EXCEEDED_ERROR } from './lapChild';
import { BILLING_RECIPIENT_TYPES, isValidNit } from './purchase/billingDocumentValidation';

const EMAIL_REGEX = {
  gfa: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
  conectagfa: /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
  default:
    /^([a-zA-Z0-9])+(((_)+|-|\.)?([a-zA-Z0-9-])+((_))?)*@([\w-]+(-|\.))*[a-zA-Z0-9]+\.[a-zA-Z]{2,}$/,
};

const isEmpty = (value) => !value || !value.toString().trim();
const join = (rules) => (value, data) =>
  rules.map((rule) => rule(value, data)).filter((error) => !!error)[0];

export function feedbackClass(field) {
  let cls = '';

  if (field.active) {
    cls = '';
  } else if (field.touched) {
    if (field.valid) {
      cls = 'isValid';
    } else {
      cls = 'has-error';
    }
  }

  return cls;
}

/**
 * Returns the validation feedback class based on the isValid and hasError parameters.
 * The classes returned come from scss styles defined at src/styles/partials/_form.scss
 *
 * @todo This method is similar to feedbackClass but is not tied to redux-form.
 * @todo We can replace feedbackClass uses with this method. This will reduce the dependency on redux-form.
 *
 * @param {boolean} isValid - The validation result.
 * @param {boolean} hasError - The error state.
 * @returns {string} The validation feedback class.
 */
export const getValidationFeedbackClass = (isValid, hasError) => {
  if (hasError) return 'has-error';
  if (isValid) return 'isValid';
  return '';
};

export function hasFeedback(field) {
  return Boolean(field.error && field.touched);
}

export function email(value) {
  if (!isEmpty(value)) {
    const { env, features } = store.getState().whitelabelConfig;

    // Use email-seems-valid if DEEP_EMAIL_VALIDATION feature is enabled
    if (features.DEEP_EMAIL_VALIDATION) {
      // emailSeemsValid returns true if email is valid, false otherwise
      if (!emailSeemsValid(value)) {
        return 'email_valid';
      }
    } else {
      // Use regex validation
      const currentRegex = EMAIL_REGEX[env?.brand] || EMAIL_REGEX.default;
      if (!currentRegex.test(value)) {
        return 'email_valid';
      }
    }
  }
}

const getRepetitionCountIn = (digit, digitsArray) => {
  const repetitionCount = digitsArray.reduce((repetitionCount, digitInArray) => {
    if (digitInArray === digit) {
      return repetitionCount + 1;
    }
    return repetitionCount;
  }, 0);
  return repetitionCount;
};

export function phone(value, fields) {
  const { phoneCountry: country } = fields;
  if (isEmpty(value)) {
    return;
  }

  const digits = value.replace(/[^\d]/g, '');
  const allDigitsAreTheSameRegex = /\b(\d)\1+\b/;
  if (allDigitsAreTheSameRegex.test(digits)) {
    return 'Invalid phone number';
  }

  const digitsArray = digits.split('');
  const hasMoreThanSixRepeatedDigits = digitsArray.some(
    (digit) => getRepetitionCountIn(digit, digitsArray) > 6,
  );

  if (hasMoreThanSixRepeatedDigits) {
    return 'Invalid phone number';
  }

  if (!isValidPhoneNumber(value, country)) {
    return `Invalid phone number for ${country}`;
  }
}

export function required(value) {
  if (value === true) {
    return;
  }
  if (value === false || isEmpty(value)) {
    return 'required';
  }
}

/**
 * Legal-terms acceptance, required only when the checkbox is actually rendered.
 *
 * The `legalTerms` field only exists under `SHOW_LEGAL_TERMS_CHECKBOX` (see
 * `AgreementCheckboxFields`). Requiring it unconditionally makes the purchaser form
 * permanently unsubmittable for every tenant that keeps the checkbox off, and because
 * the field is never rendered the error has nowhere to land: the buyer gets "fill the
 * required field" with nothing highlighted. Tenants with the checkbox on are unaffected.
 *
 * @param {*} value - Checkbox value.
 * @returns {string|undefined} 'required' when the checkbox is shown but not accepted.
 */
export function legalTermsAccepted(value) {
  const { features } = store.getState().whitelabelConfig;
  if (!features?.SHOW_LEGAL_TERMS_CHECKBOX) return undefined;
  return required(value);
}

export function minLength(min) {
  return (value) => {
    if (!isEmpty(value) && value.length < min) {
      return `Must be at least ${min} characters`;
    }
  };
}

export function maxLength(max) {
  return (value) => {
    if (!isEmpty(value) && value.length > max) {
      return `Must be no more than ${max} characters`;
    }
  };
}

export function integer(value) {
  if (!Number.isInteger(Number(value))) {
    return 'Must be an integer';
  }
}

export function match(field) {
  return (value, data) => {
    if (data) {
      if (value !== data[field]) {
        return 'Do not match';
      }
    }
  };
}

export function minWords(count) {
  return (value) => {
    if (!isEmpty(value) && value.trim().split(' ').length < count) {
      return `Must have at least ${count} words`;
    }
  };
}

export function minValue(min) {
  return (value) => {
    if (parseInt(value, 10) > parseInt(min, 10)) {
      return `Must be at least ${min}`;
    }
  };
}

export function date(value) {
  if (!moment(value, 'YYYY-MM-DD', true).isValid()) {
    return 'Invalid date';
  }
}

export function taxpayerId(value, fields) {
  // Third-party invoice flow (ENABLE_THIRD_PARTY_INVOICE): recipient-aware. A
  // third-party company (organization) requires a valid NIT; "self" needs no
  // taxpayer id. No RUC lookup.
  if (fields.billingRecipientType) {
    if (
      fields.billingRecipientType === BILLING_RECIPIENT_TYPES.ORGANIZATION &&
      !isValidNit(value)
    ) {
      return 'document_type_nit';
    }
    return undefined;
  }
  // Peru (RUC) invoice flow: 11-digit rule when the invoice toggle is on.
  if (fields.invoice && (!value || value.length < 11)) return 'Invalid taxpayer ID';
}

/**
 * Validate a Brazilian CPF taxpayer ID.
 * Returns undefined for empty values so it can be composed with required.
 *
 * @param {string} value - CPF input, with or without formatting.
 * @returns {string|undefined} 'invalid_cpf' when the value is malformed.
 */
export function cpf(value) {
  if (isEmpty(value)) return;

  const digits = value.replace(/\D/g, '');
  if (digits.length !== 11 || !cpfValidator.isValid(digits)) return 'invalid_cpf';
}

export function legalName(value, fields) {
  // Third-party invoice flow: razón social required only for a company recipient.
  if (fields.billingRecipientType) {
    if (fields.billingRecipientType === BILLING_RECIPIENT_TYPES.ORGANIZATION && !value) {
      return 'Legal name is required';
    }
    return undefined;
  }
  // Peru (RUC): legal name is auto-filled from the lookup; guard the invalid sentinel.
  if (fields.invoice && (!value || value === 'RUC Invalido')) return 'Legal name is required';
}

/**
 * Returns a regular expression pattern based on the document type.
 *
 * @param {string} type - The type of the document.
 * @returns {RegExp} The regular expression pattern for the specified document type.
 */
function getDocumentPattern(type) {
  switch (type) {
    case 'PPN':
    case 'PS':
      return /^(\w{7,12})$/i;
    case 'CC':
      return /^(\d{7,12})$/i;
    case 'CE':
      return /^(\d{7,12})$/i;
    case 'TI':
      return /^(\d{7,12})$/i;
    case 'RC':
      return /^1\d{9}$/;
    case 'PEP':
      return /^([a-zA-Z0-9]{6,})$/i;
    case 'NIT':
      return /^[1-9]\d{6,9}$/i;
    case 'DNI':
      return /^\d{8}$/i;
    default:
      return new RegExp('');
  }
}

/**
 * Validates a document ID based on the provided value and fields.
 *
 * @param {string} value - The document ID to validate.
 * @param {Object} fields - The fields containing the document type.
 * @returns {string|undefined} An error message if validation fails, otherwise undefined.
 */
export function documentId(value, fields) {
  if (isEmpty(value)) {
    return 'Document id required';
  }

  const { documentType } = fields;
  const pattern = getDocumentPattern(documentType);

  if (!pattern.test(value)) {
    if (fields.documentType === 'NIT') {
      return 'document_type_nit';
    }
    return 'Invalid format';
  }

  if (documentType === 'CPF' && !cpfValidator.isValid(value)) {
    return 'invalid cpf';
  }
  if (documentType === 'CNPJ' && !cnpj.isValid(value)) {
    return 'invalid cnpj';
  }
}

/**
 * Validates a document selected based on the provided value and fields.
 *
 * @param {string} value - The document selected to validate.
 * @returns {string|undefined} An error message if validation fails, otherwise undefined.
 */
export function documentSelected(value) {
  // Returns i18n keys, not English literals like the rest of this file. FileSelectField is the
  // only consumer of this error and it translates what it gets, so a Brazilian passenger reads
  // Portuguese instead of 'Document is required'. The other validators here keep their literals
  // because their errors are never rendered as text.
  if (isEmpty(value)) {
    return 'document_required';
  }

  const fileSizeSegment = value.split('::')[0];
  const fileSize = fileSizeSegment.split(':')[1];
  const fileSizeInMB = fileSize / (1024 * 1024);

  const isSizeValid = fileSizeInMB <= 3;

  if (!isSizeValid) {
    return 'document_max_size';
  }

  // The type, which until now was checked nowhere. An input's `accept` is only a filter on the
  // picker, so this is the last client-side check before the file leaves the funnel. It is not the
  // enforcement boundary -- that is `PassengerDocumentUploader` in platform-purchase, which now
  // checks the content type and the size where the file is stored. The MIME already travels at the
  // front of the data URL, so checking it here costs almost nothing.
  const mimeType = (value.split('::')[1] || '').match(/^data:([^;]+);/)?.[1];

  const knownType = ACCEPTED_DOCUMENT_TYPES.includes(mimeType);
  // A type the browser could not name is let through; see UNRECOGNISED_DOCUMENT_TYPE.
  const unnamedType = mimeType === UNRECOGNISED_DOCUMENT_TYPE;

  if (!knownType && !unnamedType) {
    return 'document_invalid_type';
  }
}

const adultExists = (passengers) => {
  return passengers.some((passenger) => {
    const age = getPassengerAge(passenger.dateOfBirth);
    return age >= 18;
  });
};

export const checkIsMinor = (passengers) => {
  if (!adultExists(passengers)) {
    return 'An adult must be present';
  }
};

// A minor (under 13) must travel with an adult (18+). Teens (13-17) may travel alone and do NOT
// count as an adult companion. Client-side mirror of the backend rule for better UX (blocks before submit)
// PassengersContainer#onSubmitFail surfaces the existing `adult_required` message.
//
// Tenants whose companion boundary is NOT 12 do not configure it here. The rule is owned by
// platform-purchase (MINOR_COMPANION_MAX_AGE, checked in PassengerParams and again before the seat
// lock), which is the only layer every client goes through — this one covers neither frontline nor
// the webviews. Rápido Ochoa's 0-14 boundary is enforced there; duplicating the number here is what
// let it drift in the first place.
//
// Note the asymmetry with the document rule: `minor_has_adult_companion` fails the SET, not one
// passenger, and platform-purchase adds it as `:minor_traveling_alone` — already localised by I18n,
// with no key and no index. There is nothing for parseBackendErrors to map onto a field, so a 13- or
// 14-year-old travelling alone passes this client mirror and is refused by the API with its own
// translated message in the global banner. Field-level placement would need the API to switch that
// error to the indexed form the document rule uses.
const MINOR_MAX_AGE = 12;

const hasMinorWithoutAdult = (passengers) => {
  const hasMinor = passengers.some((passenger) => {
    const age = getPassengerAge(passenger.dateOfBirth);
    return age !== null && age <= MINOR_MAX_AGE;
  });
  return hasMinor && !adultExists(passengers);
};

export const checkMinorNeedsAdult = (passengers) => {
  if (hasMinorWithoutAdult(passengers)) {
    return 'An adult must be present';
  }
};

export const checkMinorTravelMode = (passengers, passenger, age) => {
  if (isEmpty(passenger.minorTravelMode)) {
    return 'Minor Passenger Mode required';
  }
  if (passenger.minorTravelMode === 'notarialPermissionTravel' && age < 12) {
    return 'You must be 12 years or older';
  }
};

export function passengers(passengers = []) {
  const { features } = store.getState().whitelabelConfig;
  const {
    IDENTIFICATION_DOCUMENT_PER_PASSENGER: checkIdentificationDocument,
    BIRTH_DATE_PER_PASSENGER: checkBirthDate,
    NATIONALITY_PER_PASSENGER: checkNationality,
    DOCUMENT_PER_PASSENGER: checkDocumentSelect,
    GENDER_PER_PASSENGER: checkGender,
    PHONE_NUMBER_PER_PASSENGER: checkPhoneNumber,
    IDENTIFICATION_SECOND_DOCUMENT_PER_PASSENGER: checkIdentificationSecondDocument,
    MINOR_RULES: minorTravelMode,
    MINOR_VALIDATION: minorValidation,
    MINOR_NEEDS_ADULT_COMPANION: minorNeedsAdultCompanion,
    SECOND_LAST_NAME_REQUIRED_FOR_DNI: checkSecondLastNameForDNI,
    LAP_CHILD_PER_PASSENGER: checkLapChild,
    LAP_CHILD_MAX_AGE: lapChildMaxAge,
  } = features;

  const passengerErrorsArray = [];
  const passengerNames = [];

  passengers.forEach((passenger, index) => {
    const passengerError = {};
    const age = getPassengerAge(passenger.dateOfBirth);
    const emailError = email(passenger.email);

    if (index === 0 && isEmpty(passenger.email)) {
      passengerError.email = 'required';
    } else if (index === 0 && emailError) {
      passengerError.email = emailError;
    }

    if (passenger.email && emailError) {
      passengerError.email = emailError;
    }

    if (isEmpty(passenger.firstName)) {
      passengerError.firstName = 'First name required';
    }

    if (isEmpty(passenger.lastName)) {
      passengerError.lastName = 'Last name required';
    }

    // secondLastName is only requried for CDS with DNI
    if (
      isEmpty(passenger.secondLastName) &&
      checkSecondLastNameForDNI &&
      passenger.documentType === 'DNI'
    ) {
      passengerError.secondLastName = 'Last name required';
    }

    if (!isEmpty(passenger.firstName) && !isEmpty(passenger.lastName)) {
      const currentName = `${passenger.firstName} ${passenger.lastName} ${
        passenger.secondLastName ? passenger.secondLastName : ''
      }`
        .replace(/\s+/g, '')
        .toLowerCase();

      if (passengerNames.includes(currentName) && !features.ALLOW_DUPLICATED_PASSENGERS) {
        passengerError.firstName = 'duplicated name';
        passengerError.lastName = 'duplicated name';
        passengerError.secondLastName = 'duplicated name';
      }
    }

    if (!isEmpty(passenger.firstName))
      if (isEmpty(passenger.busCategory)) {
        passengerError.busCategory = 'Bus Category required';
      }

    if (checkIdentificationDocument) {
      if (isEmpty(passenger.documentType)) {
        passengerError.documentType = 'Document type required';
      }

      const duplicatedId =
        age < 18 && minorTravelMode
          ? false
          : passengers.some((el, indexEl) => {
              if (indexEl === index) return false;

              const isSameDocument = el.documentId && el.documentId === passenger.documentId;

              if (minorTravelMode) {
                const elAge = getPassengerAge(el.dateOfBirth);
                return isSameDocument && elAge >= 18;
              }
              return isSameDocument;
            });

      if (duplicatedId && !features.ALLOW_DUPLICATED_PASSENGERS) {
        passengerError.documentId = 'duplicated id';
      } else {
        passengerError.documentId = documentId(passenger.documentId, passenger);
      }
    }

    if (checkIdentificationSecondDocument) {
      if (isEmpty(passenger.documentTypeSecond)) {
        passengerError.documentTypeSecond = 'Document type required';
      }

      const duplicatedId = Boolean(
        passengers.find(
          (el, indexEl) =>
            el.documentIdSecond &&
            el.documentIdSecond === passenger.documentIdSecond &&
            indexEl !== index,
        ),
      );

      if (duplicatedId) {
        passengerError.documentIdSecond = 'duplicated id';
      } else {
        passengerError.documentIdSecond = documentId(passenger.documentIdSecond, passenger);
      }
    }

    if (checkBirthDate) {
      if (isEmpty(passenger.dateOfBirth)) {
        passengerError.dateOfBirth = 'Birth date required';
      } else if (age < 18) {
        if (minorTravelMode) {
          passengerError.minorTravelMode = checkMinorTravelMode(passengers, passenger, age);
        } else if (minorValidation) {
          passengerError.dateOfBirth = checkIsMinor(passengers);
        } else if (minorNeedsAdultCompanion) {
          passengerError.dateOfBirth = checkMinorNeedsAdult(passengers);
        }
      } else {
        passengerError.dateOfBirth = date(passenger.dateOfBirth);
      }
    }

    if (checkNationality && isEmpty(passenger.nationality)) {
      passengerError.nationality = 'Nationality is required';
    }

    if (checkDocumentSelect) {
      passengerError.document = documentSelected(passenger.document);
    }

    if (checkGender && isEmpty(passenger.gender)) {
      passengerError.gender = 'Gender is required';
    }

    if (checkPhoneNumber && isEmpty(passenger.phone)) {
      passengerError.phone = 'Phone number is required';
    }

    if (checkLapChild) {
      const lapChild = passenger.meta?.lapChild;
      const lapChildHasInput = Boolean(
        lapChild?.firstName || lapChild?.lastName || lapChild?.documentId || lapChild?.birthDate,
      );
      const childInArmsCount = Number(
        store.getState().search?.get?.('passengers')?.toJS?.()?.child_in_arms || 0,
      );
      const isFormExpanded = passenger.meta?.lapChildExpanded ?? index < childInArmsCount;
      if (lapChildHasInput || isFormExpanded) {
        const lapChildDocType = lapChild?.documentType || 'CPF';

        const validateFirstName = () =>
          isEmpty(lapChild?.firstName) ? 'First name required' : null;

        const validateLastName = () => (isEmpty(lapChild?.lastName) ? 'Last name required' : null);

        const validateDocumentId = () => {
          const pattern = getDocumentPattern(lapChildDocType);
          const documentId = lapChild?.documentId;

          if (isEmpty(documentId)) return 'Document id required';
          if (!pattern.test(documentId)) return 'Invalid format';

          return lapChildDocType === 'CPF' && !cpfValidator.isValid(documentId)
            ? 'invalid cpf'
            : null;
        };

        const validateBirthDate = () => {
          if (isEmpty(lapChild?.birthDate)) return 'Birth date required';

          const formatError = date(lapChild.birthDate);
          if (formatError) return formatError;

          const maxAge = Number(lapChildMaxAge);
          if (maxAge > 0 && getPassengerAge(lapChild.birthDate) >= maxAge) {
            return LAP_CHILD_AGE_EXCEEDED_ERROR;
          }

          return null;
        };

        const lapChildError = {
          ...(validateFirstName() && { firstName: validateFirstName() }),
          ...(validateLastName() && { lastName: validateLastName() }),
          ...(validateDocumentId() && { documentId: validateDocumentId() }),
          ...(validateBirthDate() && { birthDate: validateBirthDate() }),
        };

        if (Object.keys(lapChildError).length) {
          passengerError.meta = { lapChild: lapChildError };
        }
      }
    }

    passengerNames.push(
      `${passenger.firstName} ${passenger.lastName} ${
        passenger.secondLastName ? passenger.secondLastName : ''
      }`
        .replace(/\s+/g, '')
        .toLowerCase(),
    );

    passengerErrorsArray[index] = passengerError;
  });

  if (passengerErrorsArray.length) {
    return passengerErrorsArray;
  }
}

/**
 * Checks if an address field is optional based on whitelabel configuration.
 * @param {string} field - The address field name to check (e.g. 'zip_code', 'street_name').
 * @returns {boolean} True if the field is optional, false otherwise.
 */
function isOptionalAddressField(field) {
  const { env } = store.getState().whitelabelConfig;
  const { optionalPurchaserAddressFields = [] } = env;
  return optionalPurchaserAddressFields.includes(field);
}

/**
 * Validates a zip code field value.
 * @param {string} value - The zip code value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function zipCode(value) {
  const isOptional = isOptionalAddressField('zip_code');
  const { features } = store.getState().whitelabelConfig;

  if (!isOptional && isEmpty(value)) {
    return 'Zip code is required';
  }

  // If value is not empty and feature flag is enabled, validate numeric format
  if (!isEmpty(value) && features.ZIP_CODE_NUMERIC_ONLY) {
    const normalized = value.toString().trim();
    const numericRegex = /^\d+$/;
    if (!numericRegex.test(normalized)) {
      return 'Zip code must contain only numbers';
    }
  }
}

/**
 * Validates a street name field value.
 * @param {string} value - The street name value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function streetName(value) {
  const isOptional = isOptionalAddressField('street_name');

  if (!isOptional && isEmpty(value)) {
    return 'Street name is required';
  }
}

/**
 * Validates a street number field value.
 * @param {string} value - The street number value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function streetNumber(value) {
  const isOptional = isOptionalAddressField('street_number');

  if (!isOptional && isEmpty(value)) {
    return 'Street number is required';
  }
}

/**
 * Validates a neighborhood field value.
 * @param {string} value - The neighborhood value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function neighborhood(value) {
  const isOptional = isOptionalAddressField('neighborhood');

  if (!isOptional && isEmpty(value)) {
    return 'Neighborhood is required';
  }
}

/**
 * Validates a city field value.
 * @param {string} value - The city value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function city(value) {
  const isOptional = isOptionalAddressField('city');

  if (!isOptional && isEmpty(value)) {
    return 'City is required';
  }
}

/**
 * Validates a country field value.
 * @param {string} value - The country value to validate.
 * @returns {string|undefined} Error message if validation fails, undefined otherwise.
 */
export function country(value) {
  const isOptional = isOptionalAddressField('country');

  if (!isOptional && isEmpty(value)) {
    return 'Country is required';
  }
}

export function createValidator(rules) {
  return (data = {}) => {
    const errors = {};

    Object.keys(rules).forEach((key) => {
      if (!rules[key]) return;
      // concat enables both functions and arrays of functions
      const rule = join([].concat(rules[key]));
      const error = rule(data[key], data);

      if (error) {
        errors[key] = error;
      }
    });

    return errors;
  };
}

/**
 * Validates card fields using card-validator library.
 * Validates: holderName, cardNumber, cvv2, expirationMonth, expirationYear.
 * Validates: billingAddress if BILLING_ADDRESS_ENABLED flag is true.
 *
 * @param {Object} data - The data to validate.
 * @param {string} data.holderName - The name on the card.
 * @param {string} data.cardNumber - The card number.
 * @param {string} data.cvv2 - The CVV2 number.
 * @param {string} data.expirationMonth - The expiration month.
 * @param {string} data.expirationYear - The expiration year.
 * @param {string} data.billingAddress - The billing address.
 * @returns {Object} The errors object.
 */
export const validateCardFields = ({
  holderName,
  cardNumber,
  cvv2,
  expirationMonth,
  expirationYear,
  billingAddress,
}) => {
  const errors = {};
  const { features } = store.getState().whitelabelConfig;

  if (!holderName) {
    errors.holderName = 'Required';
  }

  if (features.BILLING_ADDRESS_ENABLED && !billingAddress) {
    errors.billingAddress = 'Required billing address';
  }

  const validatedCardNumber = valid.number(cardNumber);
  if (!validatedCardNumber.isValid) {
    errors.cardNumber = 'Invalid card number';
  }

  const amexCVVDigits = 4;
  const cvvIsValidForAmex = valid.cvv(cvv2, amexCVVDigits).isValid;
  const cvvIsValidForOtherBrands = valid.cvv(cvv2).isValid;
  if (!(cvvIsValidForAmex || cvvIsValidForOtherBrands)) {
    errors.cvv2 = 'Invalid CVC';
  }

  if (!expirationMonth || !valid.expirationMonth(expirationMonth).isValid) {
    errors.expirationMonth = 'Invalid expiry';
  }
  if (!expirationYear || !valid.expirationYear(expirationYear).isValid) {
    errors.expirationYear = 'Invalid expiry';
  }

  return errors;
};
